import { useState } from "react";
import { useStages, Stage, useDepartments } from "@/hooks/useConfig";
import { useWorkflows, useUsersWithoutWorkflow, useDepartmentsWithoutWorkflow } from "@/hooks/useWorkflows";
import { useUsers } from "@/hooks/useUsers";
import { cn } from "@/lib/utils";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { 
  Plus, 
  Pencil, 
  Trash2, 
  Loader2, 
  ChevronUp, 
  ChevronDown,
  Check,
  ChevronsUpDown,
  X,
  Search,
  Users,
  Building2,
  ArrowRight,
  GripVertical
} from "lucide-react";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { DragDropContext, Droppable, Draggable, DropResult } from "@hello-pangea/dnd";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";

export function StageManagement() {
  const {
    stages,
    isLoading,
    createStage,
    updateStage,
    deleteStage,
    reassignStageTickets,
    reorderStages,
  } = useStages();

  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);

  const [name, setName] = useState("");
  const [color, setColor] = useState("#3b82f6");
  const [selectedAssigneeIds, setSelectedAssigneeIds] = useState<string[]>([]);
  const [stageType, setStageType] = useState<'default' | 'resolved' | 'closed'>('default');
  const [editingStage, setEditingStage] = useState<Stage | null>(null);
  const [deletingStage, setDeletingStage] = useState<Stage | null>(null);
  const [ticketsToReassign, setTicketsToReassign] = useState<Array<{ id: string; ticketNumber: string; description: string }>>([]);
  const [targetStageIdForReassign, setTargetStageIdForReassign] = useState<string>("");

  // Workflow Assignment UI State: either agents OR departments (per WORK_FLOW.md)
  const [isAssignWorkflowOpen, setIsAssignWorkflowOpen] = useState(false);
  const [workflowName, setWorkflowName] = useState("");
  const [workflowAssignMode, setWorkflowAssignMode] = useState<'agents' | 'departments'>('agents');
  const [workflowUserIds, setWorkflowUserIds] = useState<string[]>([]);
  const [workflowDepartmentIds, setWorkflowDepartmentIds] = useState<string[]>([]);
  const [workflowPath, setWorkflowPath] = useState<string[]>([]);
  const [editingWorkflowId, setEditingWorkflowId] = useState<string | null>(null);
  const [isDeleteWorkflowOpen, setIsDeleteWorkflowOpen] = useState(false);
  const [deletingWorkflowId, setDeletingWorkflowId] = useState<string | null>(null);

  // Real workflows state from API
  const { workflows, isLoading: isWorkflowsLoading, createWorkflow, updateWorkflow, deleteWorkflow } = useWorkflows();

  const { users } = useUsers();
  const { departments: allDepartments = [] } = useDepartments();
  const { data: usersWithoutWorkflowResponse = [], isLoading: isUsersWithoutWorkflowLoading } = useUsersWithoutWorkflow();
  const { data: departmentsWithoutWorkflowResponse = [], isLoading: isDepartmentsWithoutWorkflowLoading } = useDepartmentsWithoutWorkflow();
  const usersWithoutWorkflow = usersWithoutWorkflowResponse || [];
  const departmentsWithoutWorkflow = Array.isArray(departmentsWithoutWorkflowResponse) ? departmentsWithoutWorkflowResponse : [];
  const editingWorkflow = editingWorkflowId ? workflows.find((w) => w.id === editingWorkflowId) : null;
  const allDepartmentsList: Array<{ id: string; name: string }> = allDepartments.map((d) => ({ id: d.id, name: d.name }));
  const baseDepartmentOptions = departmentsWithoutWorkflow.length > 0 ? departmentsWithoutWorkflow : allDepartmentsList;
  const workflowDepartmentOptions: Array<{ id: string; name: string }> = editingWorkflow?.departments?.length
    ? [
        ...editingWorkflow.departments.map((d) => ({ id: d.department.id, name: d.department.name })),
        ...baseDepartmentOptions.filter((d) => !editingWorkflow.departments?.some((ed) => ed.department.id === d.id))
      ]
    : baseDepartmentOptions;

  const toggleAssignee = (userId: string) => {
    setSelectedAssigneeIds((prev) =>
      prev.includes(userId)
        ? prev.filter((id) => id !== userId)
        : [...prev, userId]
    );
  };

  const sortedStages = [...stages].sort((a, b) => a.position - b.position);

  const handleDragEnd = (result: DropResult) => {
    if (!result.destination) return;

    const sourceIndex = result.source.index;
    const destinationIndex = result.destination.index;

    if (sourceIndex === destinationIndex) return;

    const newPath = Array.from(workflowPath);
    const [reorderedItem] = newPath.splice(sourceIndex, 1);
    newPath.splice(destinationIndex, 0, reorderedItem);

    setWorkflowPath(newPath);
  };

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!name || name.trim().length < 2) {
      toast.error("Name must be at least 2 characters");
      return;
    }

    if (!/^#[0-9A-F]{6}$/i.test(color)) {
      toast.error("Please enter a valid color hex code (e.g., #3b82f6)");
      return;
    }

    createStage.mutate(
      { name: name.trim(), color: color, position: sortedStages.length + 1, type: stageType, assigneeIds: selectedAssigneeIds },
      {
        onSuccess: () => {
          setName("");
          setColor("#3b82f6");
          setStageType("default");
          setSelectedAssigneeIds([]);
          setIsCreateOpen(false);
        },
      }
    );
  };

  const handleEdit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!editingStage) return;

    if (!name || name.trim().length < 2) {
      toast.error("Name must be at least 2 characters");
      return;
    }

    if (!/^#[0-9A-F]{6}$/i.test(color)) {
      toast.error("Please enter a valid color hex code (e.g., #3b82f6)");
      return;
    }

    updateStage.mutate(
      { id: editingStage.id, data: { name: name.trim(), color: color, type: stageType, assigneeIds: selectedAssigneeIds } },
      {
        onSuccess: () => {
          setName("");
          setColor("#3b82f6");
          setStageType("default");
          setSelectedAssigneeIds([]);
          setEditingStage(null);
          setIsEditOpen(false);
        },
      }
    );
  };

  const handleDelete = async () => {
    if (!deletingStage) return;

    try {
      const data = await deleteStage.mutateAsync(deletingStage.id);
      if (data?.success) {
        setDeletingStage(null);
        setTicketsToReassign([]);
        setTargetStageIdForReassign("");
        setIsDeleteOpen(false);
      } else if (data?.requiresReassignment) {
        setTicketsToReassign(data.tickets ?? []);
        setTargetStageIdForReassign("");
      }
    } catch {
      // Error already shown by mutation
    }
  };

  const handleReassignAndDeleteStage = () => {
    if (!deletingStage || !targetStageIdForReassign || ticketsToReassign.length === 0) return;

    reassignStageTickets.mutate(
      {
        fromStageId: deletingStage.id,
        toStageId: targetStageIdForReassign,
        ticketsIds: ticketsToReassign.map((t) => t.id),
      },
      {
        onSuccess: () => {
          setDeletingStage(null);
          setTicketsToReassign([]);
          setTargetStageIdForReassign("");
          setIsDeleteOpen(false);
        },
      }
    );
  };

  const targetStagesForReassign = sortedStages.filter((s) => s.id !== deletingStage?.id);

  const handleMoveUp = async (stage: Stage) => {
    const currentIndex = sortedStages.findIndex((s) => s.id === stage.id);
    if (currentIndex <= 0) return;

    const newStages = [...sortedStages];
    [newStages[currentIndex - 1], newStages[currentIndex]] = [
      newStages[currentIndex],
      newStages[currentIndex - 1],
    ];

    reorderStages.mutate(newStages.map((s) => s.id));
  };

  const handleMoveDown = async (stage: Stage) => {
    const currentIndex = sortedStages.findIndex((s) => s.id === stage.id);
    if (currentIndex >= sortedStages.length - 1) return;

    const newStages = [...sortedStages];
    [newStages[currentIndex], newStages[currentIndex + 1]] = [
      newStages[currentIndex + 1],
      newStages[currentIndex],
    ];

    reorderStages.mutate(newStages.map((s) => s.id));
  };

  const openEditDialog = (stage: Stage) => {
    setEditingStage(stage);
    setName(stage.name);
    setColor(stage.color);
    setStageType(stage.type || 'default');
    const ids = stage.assigneeIds?.length
      ? stage.assigneeIds
      : (stage.assignees ?? []).map((a: { userId?: string; user?: { id: string } }) => a.userId ?? a.user?.id).filter(Boolean) as string[];
    setSelectedAssigneeIds(ids);
    setIsEditOpen(true);
  };

  const openDeleteDialog = (stage: Stage) => {
    setDeletingStage(stage);
    setIsDeleteOpen(true);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex gap-2">
        <Dialog open={isCreateOpen} onOpenChange={setIsCreateOpen}>
          <DialogTrigger asChild>
            <Button>
              <Plus className="h-4 w-4 mr-2" />
              Add Stage
            </Button>
          </DialogTrigger>
          <DialogContent>
            <form onSubmit={handleCreate}>
              <DialogHeader>
                <DialogTitle>Add Workflow Stage</DialogTitle>
                <DialogDescription>
                  Create a new stage for your ticket workflow (Kanban column)
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4 py-4">
                <div>
                  <Label htmlFor="name">Stage Name</Label>
                  <Input
                    id="name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="e.g., In Progress"
                    className="mt-1"
                    autoFocus
                  />
                </div>
                <div>
                  <Label>Stage Assignees</Label>
                  <div className="mt-2 space-y-2">
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button
                          variant="outline"
                          role="combobox"
                          className="w-full justify-between h-auto min-h-10 hover:bg-background"
                        >
                          <div className="flex flex-wrap gap-1 p-1">
                            {selectedAssigneeIds.length > 0 ? (
                              selectedAssigneeIds.map((id) => {
                                const user = users.find((u) => u.id === id);
                                return (
                                  <Badge
                                    key={id}
                                    variant="secondary"
                                    className="flex items-center gap-1"
                                  >
                                    {user?.name || user?.email || "Unknown"}
                                  </Badge>
                                );
                              })
                            ) : (
                              <span className="text-muted-foreground ml-1">Select assignees...</span>
                            )}
                          </div>
                          <Search className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-full p-0" align="start">
                        <Command className="w-full">
                          <CommandInput placeholder="Search users..." />
                          <CommandList>
                            <CommandEmpty>No users found.</CommandEmpty>
                            <CommandGroup>
                              {users.map((user) => (
                                <CommandItem
                                  key={user.id}
                                  onSelect={() => toggleAssignee(user.id)}
                                  className="flex items-center gap-2"
                                >
                                  <div
                                    className={cn(
                                      "flex h-4 w-4 items-center justify-center rounded-sm border border-primary",
                                      selectedAssigneeIds.includes(user.id)
                                        ? "bg-primary text-primary-foreground"
                                        : "opacity-50 [&_svg]:invisible"
                                    )}
                                  >
                                    <Check className="h-3 w-3" />
                                  </div>
                                  <span>{user.name || user.email}</span>
                                </CommandItem>
                              ))}
                            </CommandGroup>
                          </CommandList>
                        </Command>
                      </PopoverContent>
                    </Popover>
                    <p className="text-xs text-muted-foreground">
                      Assignees will be automatically assigned to tickets entering this stage
                    </p>
                  </div>
                </div>
                <div>
                  <Label htmlFor="color">Color</Label>
                  <div className="flex gap-2 mt-1">
                    <Input
                      id="color"
                      type="color"
                      value={color}
                      onChange={(e) => setColor(e.target.value)}
                      className="w-20 h-10 cursor-pointer"
                    />
                    <Input
                      value={color}
                      onChange={(e) => setColor(e.target.value)}
                      placeholder="#3b82f6"
                      className="flex-1"
                    />
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">
                    Choose a color for this stage (used in Kanban board)
                  </p>
                </div>
                <div>
                  <Label htmlFor="type">Stage Type</Label>
                  <Select value={stageType} onValueChange={(value: 'default' | 'resolved' | 'closed') => setStageType(value)}>
                    <SelectTrigger className="mt-1">
                      <SelectValue placeholder="Select type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="default">Default</SelectItem>
                      <SelectItem value="resolved">Resolved</SelectItem>
                      <SelectItem value="closed">Closed</SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground mt-1">
                    Special types are handled specifically by the system (e.g., ticket resolution)
                  </p>
                </div>
              </div>
              <DialogFooter>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    setName("");
                    setColor("#3b82f6");
                    setStageType("default");
                    setIsCreateOpen(false);
                  }}
                >
                  Cancel
                </Button>
                <Button type="submit" disabled={createStage.isPending}>
                  {createStage.isPending && (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  )}
                  Create
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        <Dialog
          open={isAssignWorkflowOpen}
          onOpenChange={(open) => {
            setIsAssignWorkflowOpen(open);
            if (!open) {
              setEditingWorkflowId(null);
              setWorkflowAssignMode("agents");
              setWorkflowUserIds([]);
              setWorkflowDepartmentIds([]);
              setWorkflowPath([]);
              setWorkflowName("");
            } else if (!editingWorkflowId) {
              setWorkflowAssignMode("agents");
              setWorkflowUserIds([]);
              setWorkflowDepartmentIds([]);
            }
          }}
        >
          <DialogTrigger asChild>
            <Button variant="outline">
              <Users className="h-4 w-4 mr-2" />
              Assign Workflow
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[90vh] flex flex-col p-0 overflow-hidden">
            <DialogHeader className="p-6 pb-0">
              <DialogTitle>{editingWorkflowId ? "Edit" : "Assign"} Agent Workflow</DialogTitle>
              <DialogDescription>
                Configure a custom stage sequence for a specific agent.
              </DialogDescription>
            </DialogHeader>
            <div className="flex-1 overflow-y-auto p-6 space-y-6">
              <div className="space-y-2">
                <Label htmlFor="workflow-name">Workflow Name</Label>
                <Input
                  id="workflow-name"
                  value={workflowName}
                  onChange={(e) => setWorkflowName(e.target.value)}
                  placeholder="e.g., Senior Agent Flow"
                />
              </div>

              <div className="space-y-3">
                <Label>Assign to</Label>
                <div className="flex gap-4">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="workflow-assign-mode"
                      checked={workflowAssignMode === 'agents'}
                      onChange={() => {
                        setWorkflowAssignMode('agents');
                        setWorkflowDepartmentIds([]);
                      }}
                      className="text-primary"
                    />
                    <Users className="h-4 w-4" />
                    <span>Agents</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="workflow-assign-mode"
                      checked={workflowAssignMode === 'departments'}
                      onChange={() => {
                        setWorkflowAssignMode('departments');
                        setWorkflowUserIds([]);
                      }}
                      className="text-primary"
                    />
                    <Building2 className="h-4 w-4" />
                    <span>Departments</span>
                  </label>
                </div>
              </div>

              {workflowAssignMode === 'agents' && (
                <div className="space-y-2">
                  <Label>Select Agents</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        role="combobox"
                        className="w-full justify-between h-auto min-h-10"
                        disabled={!editingWorkflowId && isUsersWithoutWorkflowLoading}
                      >
                        <div className="flex flex-wrap gap-1 p-1">
                          {workflowUserIds.length > 0 ? (
                            workflowUserIds.map((id) => {
                              const user = users.find((u) => u.id === id);
                              return (
                                <Badge key={id} variant="secondary" className="gap-1">
                                  {user?.profile?.name || user?.name || user?.email || id}
                                  <X className="h-3 w-3 cursor-pointer" onClick={(e) => { e.stopPropagation(); setWorkflowUserIds((p) => p.filter((x) => x !== id)); }} />
                                </Badge>
                              );
                            })
                          ) : (
                            <span className="text-muted-foreground">
                              {!editingWorkflowId && isUsersWithoutWorkflowLoading ? "Loading agents..." : "Search and select agents..."}
                            </span>
                          )}
                        </div>
                        <Search className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0" align="start">
                      <Command>
                        <CommandInput placeholder="Search agents..." />
                        <CommandList>
                          <CommandEmpty>No agents found.</CommandEmpty>
                          <CommandGroup>
                            {(editingWorkflowId ? users : usersWithoutWorkflow).map((u: { id: string; email: string; name?: string; profile?: { name?: string } }) => (
                              <CommandItem
                                key={u.id}
                                onSelect={() => {
                                  setWorkflowUserIds((prev) =>
                                    prev.includes(u.id) ? prev.filter((id) => id !== u.id) : [...prev, u.id]
                                  );
                                }}
                              >
                                <div
                                  className={cn(
                                    "mr-2 flex h-4 w-4 items-center justify-center rounded-sm border border-primary",
                                    workflowUserIds.includes(u.id) ? "bg-primary text-primary-foreground" : "opacity-50 [&_svg]:invisible"
                                  )}
                                >
                                  <Check className="h-4 w-4" />
                                </div>
                                {u.profile?.name || (u as { name?: string }).name || u.email}
                              </CommandItem>
                            ))}
                          </CommandGroup>
                        </CommandList>
                      </Command>
                    </PopoverContent>
                  </Popover>
                </div>
              )}

              {workflowAssignMode === 'departments' && (
                <div className="space-y-2">
                  <Label>Select Departments</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        role="combobox"
                        className="w-full justify-between h-auto min-h-10"
                        disabled={workflowDepartmentOptions.length === 0}
                      >
                        <div className="flex flex-wrap gap-1 p-1">
                          {workflowDepartmentIds.length > 0 ? (
                            workflowDepartmentIds.map((id) => {
                              const dept = workflowDepartmentOptions.find((d) => d.id === id);
                              return (
                                <Badge key={id} variant="secondary" className="gap-1">
                                  {dept?.name ?? id}
                                  <X className="h-3 w-3 cursor-pointer" onClick={(e) => { e.stopPropagation(); setWorkflowDepartmentIds((p) => p.filter((x) => x !== id)); }} />
                                </Badge>
                              );
                            })
                          ) : (
                            <span className="text-muted-foreground">
                              {!editingWorkflowId && isDepartmentsWithoutWorkflowLoading ? "Loading departments..." : "Search and select departments..."}
                            </span>
                          )}
                        </div>
                        <Search className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0" align="start">
                      <Command shouldFilter={true} className="rounded-lg border shadow-md">
                        <CommandInput placeholder="Search departments..." />
                        <CommandList>
                          <CommandEmpty>No departments found.</CommandEmpty>
                          <CommandGroup>
                            {workflowDepartmentOptions.map((d) => (
                              <CommandItem
                                key={d.id}
                                value={`${d.name} ${d.id}`}
                                onSelect={() => {
                                  setWorkflowDepartmentIds((prev) =>
                                    prev.includes(d.id) ? prev.filter((id) => id !== d.id) : [...prev, d.id]
                                  );
                                }}
                              >
                                <div
                                  className={cn(
                                    "mr-2 flex h-4 w-4 items-center justify-center rounded-sm border border-primary",
                                    workflowDepartmentIds.includes(d.id) ? "bg-primary text-primary-foreground" : "opacity-50 [&_svg]:invisible"
                                  )}
                                >
                                  <Check className="h-4 w-4" />
                                </div>
                                {d.name}
                              </CommandItem>
                            ))}
                          </CommandGroup>
                        </CommandList>
                      </Command>
                    </PopoverContent>
                  </Popover>
                  <p className="text-xs text-muted-foreground">
                  {departmentsWithoutWorkflow.length > 0
                    ? "Departments without a workflow are listed. You can select multiple."
                    : "All departments are listed. You can select multiple."}
                </p>
                </div>
              )}

              <div className="grid grid-cols-2 gap-6">
                <div className="space-y-3">
                  <Label className="text-sm font-semibold">Available Stages</Label>
                  <div className="border rounded-md p-2 h-[200px] overflow-y-auto space-y-1 bg-muted/20">
                    {sortedStages.map((stage) => {
                      const isSelected = workflowPath.includes(stage.id);
                      return (
                        <Button
                          key={stage.id}
                          variant="ghost"
                          className={cn(
                            "w-full justify-start h-9 text-sm font-normal px-2",
                            isSelected && "opacity-50 cursor-not-allowed bg-muted"
                          )}
                          disabled={isSelected}
                          onClick={() => setWorkflowPath([...workflowPath, stage.id])}
                        >
                          <Plus className="h-3 w-3 mr-2" />
                          {stage.name}
                        </Button>
                      );
                    })}
                  </div>
                </div>

                <div className="space-y-3">
                  <Label className="text-sm font-semibold">Workflow Path (In Order)</Label>
                  <DragDropContext onDragEnd={handleDragEnd}>
                    <Droppable droppableId="workflow-path">
                      {(provided) => (
                        <div 
                          className="border rounded-md p-2 h-[200px] overflow-y-auto space-y-2 bg-background"
                          {...provided.droppableProps}
                          ref={provided.innerRef}
                        >
                          {workflowPath.length === 0 ? (
                            <div className="h-full flex flex-col items-center justify-center text-muted-foreground text-xs p-4 text-center">
                              <ArrowRight className="h-8 w-8 mb-2 opacity-20" />
                              Click stages on the left to build the sequence.
                            </div>
                          ) : (
                            workflowPath.map((stageId, index) => {
                              const stage = stages.find((s) => s.id === stageId);
                              return (
                                <Draggable key={`${stageId}-${index}`} draggableId={`${stageId}-${index}`} index={index}>
                                  {(provided, snapshot) => (
                                    <div 
                                      ref={provided.innerRef}
                                      {...provided.draggableProps}
                                      {...provided.dragHandleProps}
                                      className={cn(
                                        "flex items-center gap-2 p-2 rounded-md border text-sm",
                                        snapshot.isDragging ? "bg-secondary/80 shadow-md ring-1 ring-primary/20 z-10" : "bg-secondary/50"
                                      )}
                                    >
                                      <div className="flex items-center justify-center cursor-grab text-muted-foreground hover:text-foreground">
                                        <GripVertical className="h-4 w-4" />
                                      </div>
                                      <div className="flex h-5 w-5 items-center justify-center rounded-full bg-primary text-[10px] text-primary-foreground font-bold">
                                        {index + 1}
                                      </div>
                                      <span className="flex-1 font-medium truncate">{stage?.name}</span>
                                      <Button 
                                        variant="ghost" 
                                        size="icon" 
                                        className="h-6 w-6 hover:bg-destructive/10 hover:text-destructive"
                                        onClick={() => {
                                          const newPath = [...workflowPath];
                                          newPath.splice(index, 1);
                                          setWorkflowPath(newPath);
                                        }}
                                      >
                                        <X className="h-3 w-3" />
                                      </Button>
                                    </div>
                                  )}
                                </Draggable>
                              );
                            })
                          )}
                          {provided.placeholder}
                        </div>
                      )}
                    </Droppable>
                  </DragDropContext>
                </div>
              </div>

              {workflowPath.length > 1 && (
                <div className="flex items-center gap-2 p-3 bg-primary/5 rounded-lg border border-primary/20">
                  <div className="flex-1 flex flex-wrap items-center gap-2 text-xs">
                    <span className="font-semibold text-primary">Preview:</span>
                    {workflowPath.map((stageId, idx) => (
                      <div key={stageId} className="flex items-center gap-1">
                        <span className="px-2 py-0.5 rounded-full bg-background border">
                          {stages.find((s) => s.id === stageId)?.name}
                        </span>
                        {idx < workflowPath.length - 1 && <ArrowRight className="h-3 w-3 text-muted-foreground" />}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
            <DialogFooter className="p-6 pt-0">
              <Button
                variant="outline"
                onClick={() => {
                  setWorkflowName("");
                  setWorkflowAssignMode("agents");
                  setWorkflowUserIds([]);
                  setWorkflowDepartmentIds([]);
                  setWorkflowPath([]);
                  setEditingWorkflowId(null);
                  setIsAssignWorkflowOpen(false);
                }}
              >
                Cancel
              </Button>
              <Button 
                onClick={async () => {
                  try {
                    const stagePayload = workflowPath.map((stageId, index) => ({
                      stageId,
                      position: index + 1
                    }));
                    const hasAgents = workflowAssignMode === "agents" && workflowUserIds.length > 0;
                    const hasDepartments = workflowAssignMode === "departments" && workflowDepartmentIds.length > 0;
                    if (!hasAgents && !hasDepartments) {
                      toast.error("Select at least one agent or one department.");
                      return;
                    }

                    if (editingWorkflowId) {
                      await updateWorkflow.mutateAsync({
                        id: editingWorkflowId,
                        data: {
                          name: workflowName || "Untitled Workflow",
                          ...(workflowAssignMode === "agents" ? { userIds: workflowUserIds } : { departmentsIds: workflowDepartmentIds }),
                          stages: stagePayload
                        }
                      });
                    } else {
                      await createWorkflow.mutateAsync({
                        name: workflowName || "Untitled Workflow",
                        ...(workflowAssignMode === "agents" ? { userIds: workflowUserIds } : { departmentsIds: workflowDepartmentIds }),
                        stages: stagePayload
                      });
                    }
                    
                    setWorkflowName("");
                    setWorkflowAssignMode("agents");
                    setWorkflowUserIds([]);
                    setWorkflowDepartmentIds([]);
                    setWorkflowPath([]);
                    setEditingWorkflowId(null);
                    setIsAssignWorkflowOpen(false);
                  } catch (e) {
                    // Error is handled by the mutation's onError toast
                  }
                }}
                disabled={
                  workflowPath.length === 0 ||
                  (workflowAssignMode === "agents" ? workflowUserIds.length === 0 : workflowDepartmentIds.length === 0) ||
                  createWorkflow.isPending ||
                  updateWorkflow.isPending
                }
              >
                {(createWorkflow.isPending || updateWorkflow.isPending) && (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                )}
                {editingWorkflowId ? "Update Workflow" : "Save Workflow"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <Dialog open={isEditOpen} onOpenChange={setIsEditOpen}>
        <DialogContent>
          <form onSubmit={handleEdit}>
            <DialogHeader>
              <DialogTitle>Edit Workflow Stage</DialogTitle>
              <DialogDescription>
                Update the stage name and color
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div>
                <Label htmlFor="edit-name">Stage Name</Label>
                <Input
                  id="edit-name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="mt-1"
                  autoFocus
                />
              </div>
              <div>
                <Label>Stage Assignees</Label>
                <div className="mt-2 space-y-2">
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        role="combobox"
                        className="w-full justify-between h-auto min-h-10 hover:bg-background"
                      >
                        <div className="flex flex-wrap gap-1 p-1">
                          {selectedAssigneeIds.length > 0 ? (
                            selectedAssigneeIds.map((id) => {
                              const fromStage = editingStage?.assignees?.find((a: { userId?: string; user?: { id: string; email?: string; profile?: { name?: string } } }) => a.userId === id || a.user?.id === id);
                              const fromUsers = users.find((u) => u.id === id);
                              const displayName = fromStage?.user
                                ? (fromStage.user.profile?.name || fromStage.user.email || "Unknown")
                                : (fromUsers?.profile?.name || (fromUsers as { name?: string })?.name || fromUsers?.email || "Unknown");
                              return (
                                <Badge
                                  key={id}
                                  variant="secondary"
                                  className="flex items-center gap-1"
                                >
                                  {displayName}
                                </Badge>
                              );
                            })
                          ) : (
                            <span className="text-muted-foreground ml-1">Select assignees...</span>
                          )}
                        </div>
                        <Search className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0" align="start">
                      <Command className="w-full">
                        <CommandInput placeholder="Search users..." />
                        <CommandList>
                          <CommandEmpty>No users found.</CommandEmpty>
                          <CommandGroup>
                            {users.map((user) => (
                              <CommandItem
                                key={user.id}
                                onSelect={() => toggleAssignee(user.id)}
                                className="flex items-center gap-2"
                              >
                                <div
                                  className={cn(
                                    "flex h-4 w-4 items-center justify-center rounded-sm border border-primary",
                                    selectedAssigneeIds.includes(user.id)
                                      ? "bg-primary text-primary-foreground"
                                      : "opacity-50 [&_svg]:invisible"
                                  )}
                                >
                                  <Check className="h-3 w-3" />
                                </div>
                                <span>{user.profile?.name || user.name || user.email}</span>
                              </CommandItem>
                            ))}
                          </CommandGroup>
                        </CommandList>
                      </Command>
                    </PopoverContent>
                  </Popover>
                  <p className="text-xs text-muted-foreground">
                    Assignees will be automatically assigned to tickets entering this stage
                  </p>
                </div>
              </div>
              <div>
                <Label htmlFor="edit-color">Color</Label>
                <div className="flex gap-2 mt-1">
                  <Input
                    id="edit-color"
                    type="color"
                    value={color}
                    onChange={(e) => setColor(e.target.value)}
                    className="w-20 h-10 cursor-pointer"
                  />
                  <Input
                    value={color}
                    onChange={(e) => setColor(e.target.value)}
                    placeholder="#3b82f6"
                    className="flex-1"
                  />
                </div>
              </div>
              <div>
                <Label htmlFor="edit-type">Stage Type</Label>
                <Select value={stageType} onValueChange={(value: 'default' | 'resolved' | 'closed') => setStageType(value)}>
                  <SelectTrigger className="mt-1">
                    <SelectValue placeholder="Select type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="default">Default</SelectItem>
                    <SelectItem value="resolved">Resolved</SelectItem>
                    <SelectItem value="closed">Closed</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setName("");
                  setColor("#3b82f6");
                  setStageType("default");
                  setEditingStage(null);
                  setIsEditOpen(false);
                }}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={updateStage.isPending}>
                {updateStage.isPending && (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                )}
                Update
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <AlertDialog
        open={isDeleteOpen}
        onOpenChange={(open) => {
          if (!open) {
            setDeletingStage(null);
            setTicketsToReassign([]);
            setTargetStageIdForReassign("");
          }
          setIsDeleteOpen(open);
        }}
      >
        <AlertDialogContent>
          {ticketsToReassign.length === 0 ? (
            <>
              <AlertDialogHeader>
                <AlertDialogTitle>Delete stage?</AlertDialogTitle>
                <AlertDialogDescription>
                  This will delete &quot;{deletingStage?.name}&quot;. This action cannot be undone.
                  If this stage has tickets, you will need to choose another stage to move them to first.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel
                  onClick={() => {
                    setDeletingStage(null);
                  }}
                >
                  Cancel
                </AlertDialogCancel>
                <Button
                  type="button"
                  variant="destructive"
                  disabled={deleteStage.isPending}
                  onClick={(e) => {
                    e.preventDefault();
                    handleDelete();
                  }}
                >
                  {deleteStage.isPending && (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  )}
                  Delete
                </Button>
              </AlertDialogFooter>
            </>
          ) : (
            <>
              <AlertDialogHeader>
                <AlertDialogTitle>Move tickets to another stage</AlertDialogTitle>
                <AlertDialogDescription>
                  &quot;{deletingStage?.name}&quot; has {ticketsToReassign.length} ticket
                  {ticketsToReassign.length !== 1 ? "s" : ""}. Choose a stage to move them to, then the stage will be deleted.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <div className="space-y-4 py-2">
                <div className="max-h-32 overflow-y-auto rounded border bg-muted/30 p-2 text-sm">
                  {ticketsToReassign.map((t) => (
                    <div key={t.id} className="flex justify-between gap-2 py-1">
                      <span className="font-medium">{t.ticketNumber}</span>
                      <span className="truncate text-muted-foreground">{t.description?.slice(0, 50)}</span>
                    </div>
                  ))}
                </div>
                <div>
                  <Label>Move tickets to stage</Label>
                  <Select
                    value={targetStageIdForReassign}
                    onValueChange={setTargetStageIdForReassign}
                  >
                    <SelectTrigger className="mt-1">
                      <SelectValue placeholder="Select stage..." />
                    </SelectTrigger>
                    <SelectContent>
                      {targetStagesForReassign.map((s) => (
                        <SelectItem key={s.id} value={s.id}>
                          {s.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <AlertDialogFooter>
                <AlertDialogCancel
                  onClick={() => {
                    setDeletingStage(null);
                    setTicketsToReassign([]);
                    setTargetStageIdForReassign("");
                    setIsDeleteOpen(false);
                  }}
                >
                  Cancel
                </AlertDialogCancel>
                <AlertDialogAction
                  onClick={handleReassignAndDeleteStage}
                  disabled={!targetStageIdForReassign || reassignStageTickets.isPending}
                  className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                >
                  {reassignStageTickets.isPending && (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  )}
                  Reassign and delete stage
                </AlertDialogAction>
              </AlertDialogFooter>
            </>
          )}
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={isDeleteWorkflowOpen} onOpenChange={setIsDeleteWorkflowOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove Agent Workflow?</AlertDialogTitle>
            <AlertDialogDescription>
              This will remove the custom workflow assignment for this agent. 
              This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setDeletingWorkflowId(null)}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={async () => {
                if (deletingWorkflowId) {
                  try {
                    await deleteWorkflow.mutateAsync(deletingWorkflowId);
                    setDeletingWorkflowId(null);
                    setIsDeleteWorkflowOpen(false);
                  } catch (e) {
                     // Error handled by the hook
                  }
                } else {
                  setDeletingWorkflowId(null);
                  setIsDeleteWorkflowOpen(false);
                }
              }}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              disabled={deleteWorkflow.isPending}
            >
              {deleteWorkflow.isPending && (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              )}
              Remove
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <div className="border rounded-lg">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead>Color</TableHead>
              <TableHead>Position</TableHead>
              <TableHead>Created</TableHead>
              <TableHead className="w-[180px]">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {sortedStages.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} className="text-center text-muted-foreground py-8">
                  No stages yet. Create one to get started.
                </TableCell>
              </TableRow>
            ) : (
              sortedStages.map((stage, index) => (
                <TableRow key={stage.id}>
                  <TableCell className="font-medium">{stage.name}</TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <div
                        className="w-6 h-6 rounded border"
                        style={{ backgroundColor: stage.color }}
                      />
                      <span className="text-xs text-muted-foreground">
                        {stage.color}
                      </span>
                    </div>
                  </TableCell>
                  <TableCell>{stage.position}</TableCell>
                  <TableCell>
                    {stage.created_at
                      ? new Date(stage.created_at).toLocaleDateString()
                      : "-"}
                  </TableCell>
                  <TableCell>
                    <div className="flex gap-1">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleMoveUp(stage)}
                        disabled={index === 0}
                      >
                        <ChevronUp className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleMoveDown(stage)}
                        disabled={index === sortedStages.length - 1}
                      >
                        <ChevronDown className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => openEditDialog(stage)}
                      >
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => openDeleteDialog(stage)}
                      >
                        <Trash2 className="h-4 w-4 text-destructive" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      <div className="mt-8 space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="text-xl font-bold flex items-center gap-2">
            <Users className="h-5 w-5" />
            Agent Workflows
          </h2>
        </div>
        
        <div className="border rounded-lg">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Workflow Name</TableHead>
                <TableHead>Assigned To</TableHead>
                <TableHead>Path</TableHead>
                <TableHead>Created</TableHead>
                <TableHead className="w-[120px]">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isWorkflowsLoading ? (
                 <TableRow>
                   <TableCell colSpan={5} className="text-center py-8">
                     <Loader2 className="h-6 w-6 animate-spin mx-auto text-muted-foreground" />
                   </TableCell>
                 </TableRow>
              ) : workflows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} className="text-center text-muted-foreground py-8">
                    No agent workflows defined yet.
                  </TableCell>
                </TableRow>
              ) : (
                workflows.map((wf) => {
                  const agents = (wf.users ?? []).map((wu) => wu?.user).filter(Boolean) as Array<{ id: string; email?: string; name?: string; profile?: { name?: string } }>;
                  const depts = (wf as { departments?: Array<{ department: { id: string; name: string } }> }).departments ?? [];
                  const sortedStages = [...(wf.workflowStages || [])].sort((a, b) => a.position - b.position);
                  
                  return (
                    <TableRow key={wf.id}>
                      <TableCell className="font-medium">{wf.name}</TableCell>
                      <TableCell>
                        <div className="flex flex-wrap gap-1">
                          {agents.length > 0 ? (
                            agents.map((a) => (
                              <Badge key={a.id} variant="secondary" className="text-xs font-normal">
                                {a.profile?.name || a.name || a.email || a.id}
                              </Badge>
                            ))
                          ) : depts.length > 0 ? (
                            depts.map((d) => (
                              <Badge key={d.department.id} variant="outline" className="text-xs font-normal">
                                {d.department.name}
                              </Badge>
                            ))
                          ) : (
                            <span className="text-muted-foreground">Not assigned</span>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1 flex-wrap">
                          {sortedStages.map((ws, idx) => (
                            <div key={ws.stage.id} className="flex items-center gap-1">
                              <Badge variant="outline" className="text-[10px] font-normal" style={{ borderColor: ws.stage.color, color: ws.stage.color }}>
                                {ws.stage.name}
                              </Badge>
                              {idx < sortedStages.length - 1 && <ArrowRight className="h-3 w-3 opacity-30" />}
                            </div>
                          ))}
                        </div>
                      </TableCell>
                      <TableCell className="text-muted-foreground">
                        {new Date(wf.createdAt).toLocaleDateString()}
                      </TableCell>
                      <TableCell>
                        <div className="flex gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              setWorkflowName(wf.name);
                              const hasDepartments = (wf as { departments?: Array<{ department: { id: string; name: string } }> }).departments?.length;
                              if (hasDepartments) {
                                setWorkflowAssignMode("departments");
                                setWorkflowDepartmentIds((wf as { departments: Array<{ department: { id: string } }> }).departments.map((d) => d.department.id));
                                setWorkflowUserIds([]);
                              } else {
                                setWorkflowAssignMode("agents");
                                setWorkflowUserIds(agents.map((a) => a.id));
                                setWorkflowDepartmentIds([]);
                              }
                              setWorkflowPath(sortedStages.map(ws => ws.stage.id));
                              setEditingWorkflowId(wf.id);
                              setIsAssignWorkflowOpen(true);
                            }}
                          >
                            <Pencil className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              setDeletingWorkflowId(wf.id);
                              setIsDeleteWorkflowOpen(true);
                            }}
                          >
                            <Trash2 className="h-4 w-4 text-destructive" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </div>
      </div>
    </div>
  );
}
